import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

// Observable
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/throw';
import { Observable } from 'rxjs/Observable';

import { emptyLogin, ILogin } from '../model/login';
import { emptyPerson, IPerson } from '../model/person';
import { SpinnerService } from './spinner.service';
import { PersonService } from "./person.service";
import { BehaviorSubject } from "rxjs/BehaviorSubject";

@Injectable()
export class LoginService {
  private loginCertUrl = environment.apiUrl + '/login-cert';
  private loginClaveUrl = environment.apiUrl + '/login-clave/form';
  private loginDevUrl = environment.apiUrl + '/login/dev';

  /**
   * Indica si abrir modal de login,
   * para el caso de intentar acceder a una página privada sin estar identificado
   */
  public openLoginModal: boolean = false;

  /**
   * Url de redirección, hay que guardar en el local store porque al ir a clave se pierde
   */
  private redirectUrl: string = null;

  /**
   * Usuario logueado
   *
   * @type {BehaviorSubject<IPerson>}
   */
  private personSource = new BehaviorSubject<IPerson>(emptyPerson);
  /**
   * Usuario logueado
   *
   * @type {Observable<IPerson>}
   */
  person$ = this.personSource.asObservable();

  /**
   *
   * @param {HttpClient} http
   * @param {JwtHelperService} jwtHelper
   * @param {Router} router
   * @param {SpinnerService} spinnerService
   * @param personService
   */
  constructor(
    private http: HttpClient,
    private jwtHelper: JwtHelperService,
    private router: Router,
    private spinnerService: SpinnerService,
    private personService: PersonService,
  ) {
    this.personSource.next(this.getPerson());
  }

  loginDev(): Observable<string> {
    return this.http.get(this.loginDevUrl).map(res => {
      return res as string;
    });
  }

  loginCert(): Observable<ILogin> {
    this.spinnerService.setSpinner(true);
    return this.http.get(this.loginCertUrl)
      .map(res => {
        this.spinnerService.setSpinner(false);
        return res as ILogin;
      })
      .catch(error => {
        this.spinnerService.setSpinner(false);
        return Observable.throw(error);
      });
  }

  /**
   * Devuelve el formulario de login a traves de clave
   */
  getClaveForm(): Observable<string> {
    this.spinnerService.setSpinner(true);
    return this.http.get(this.loginClaveUrl)
      .map(res => {
        this.spinnerService.setSpinner(false);
        return res as string;
      })
      .catch(error => {
        this.spinnerService.setSpinner(false);
        return Observable.throw(error);
      });
  }

  /**
   * Guardar datos de login y redirigir
   */
  login(login: ILogin, refreshPerson: boolean = false) {
    this.setLoginData(login);
    if (login.token) {
      // En login con clave no se devuelven todos los datos y hay que consultar el usuario logueado
      this.personService.getPersonAuthenticated()
        .subscribe(person => {
          let login = emptyLogin;
          login.person = person;
          this.setLoginData(login);
          if (person && (person.code || person.authorizedOfInvoicingService)) {
            this.redirectToUrl();
          }
        });
    }
  }

  /**
   * Guardar datos de login y redirigir
   */
  login$(login: ILogin) {
    this.setLoginData(login);
    if (login.token) {
      // En login con clave no se devuelven todos los datos y hay que consultar el usuario logueado
      return this.personService.getPersonAuthenticated();
    }
  }

  /**
   * Devuelve si el usuario esta logueado
   */
  isLogged(): boolean {
    if (!localStorage.getItem('token') || !localStorage.getItem('person')) {
      return false;
    }
    return !this.jwtHelper.isTokenExpired();
  }

  removeToken(): void {
    localStorage.removeItem('token');
  }

  /**
   * Logout
   */
  logout() {
    localStorage.clear()
    this.personSource.next(null);
  }

  /**
   * Guarda los datos de usuario en el local storage
   */
  setLoginData(login: ILogin) {
    if (login.person) {
      localStorage.setItem('person', JSON.stringify(login.person));
      this.personSource.next(login.person);
    }
    if (login.token) {
      localStorage.setItem('token', login.token);
    }
  }

  /**
   * Devuelve el usuario logueado
   */
  getPerson(): IPerson {
    return JSON.parse(localStorage.getItem('person')) as IPerson;
  }

  /**
   * Obtiene la información de la persona logueada
   */
  rechargePersonInfo() {
    this.personService.getPersonAuthenticated()
      .subscribe(
        res => {
          this.personSource.next(res);
          localStorage.removeItem('person');
          localStorage.setItem('person', JSON.stringify(res));
        });
  }

  /**
   * Eliminar url de redirección
   */
  removeRedirectUrl() {
    localStorage.removeItem('redirectUrl');
    this.redirectUrl = null;
    // Si no hay url de redireción no tiene sentido abrir la modal en el portal
    this.openLoginModal = false;
  }

  /**
   * Guardar url de redirección
   */
  setRedirectUrl(url: string) {
    localStorage.setItem('redirectUrl', url);
    this.redirectUrl = url;
    // Al fijar url de redireción abrimos el modal del login directamente
    this.openLoginModal = true;
  }

  /**
   * Guardar url de redirección de cliente
   */
  setRedirectCliUrl() {
    this.setRedirectUrl('/cliente');
  }

  /**
   * Guardar url de redirección de empresa
   */
  setRedirectInvUrl() {
    this.setRedirectUrl('/empresas');
  }

  /**
   * Devuelve la url de redirección, en caso de no existir devuelve una por defecto
   */
  getRedirectUrl(): string {
    if (!this.redirectUrl) {
      this.redirectUrl = localStorage.getItem('redirectUrl');
    }

    return this.redirectUrl ? this.redirectUrl :
      ('/empresas');
  }

  redirectToUrl() {
    this.router.navigate([this.getRedirectUrl()])
      .then(() => this.removeRedirectUrl());
  }

  redirectToHome() {
    this.router.navigate(['/']).then();
  }

  hasDireCode(): boolean {
    return this.getPerson() && !this.getPerson().code;
  }
  isAuthorized(): boolean {
    return this.getPerson() && this.getPerson().authorizedOfInvoicingService;
  }
}



// WEBPACK FOOTER //
// ./src/app/services/login.service.ts